import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CreditCardIcon,
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  EyeIcon,
  Cog6ToothIcon,
  BellIcon,
  LightBulbIcon,
  BanknotesIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  MegaphoneIcon,
  SparklesIcon,
  FireIcon,
  ShieldExclamationIcon,
  ArrowPathIcon,
  Squares2X2Icon,
  ListBulletIcon,
  CpuChipIcon,
  CogIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { smartCardsData, SmartCard, SmartCardInsight, formatCurrency } from '../data/smartCardsData';
import { useLanguage } from '../contexts/LanguageContext';
import AIAnalysisEngine from '../components/smart-cards/AIAnalysisEngine';
import SmartRecommendations from '../components/smart-cards/SmartRecommendations';
import SmartMetrics from '../components/smart-cards/SmartMetrics';

const SmartCardsPage: React.FC = () => {
  const { t, direction } = useLanguage();
  const [cards, setCards] = useState<SmartCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCard, setSelectedCard] = useState<SmartCard | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Load smart cards data
  useEffect(() => {
    setTimeout(() => {
      setCards(smartCardsData);
      setLoading(false);
    }, 1500);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'from-green-500 to-green-600';
      case 'warning': return 'from-yellow-500 to-orange-500';
      case 'critical': return 'from-red-500 to-red-600';
      case 'optimized': return 'from-blue-500 to-purple-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400';
      case 'medium': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low': return 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400';
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  // Filter and search logic
  const filteredCards = cards.filter(card => {
    // Apply type filter
    if (filterType !== 'all' && card.type !== filterType) {
      return false;
    }
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return card.title.toLowerCase().includes(searchLower) ||
             card.insights.some((insight: any) => 
               insight.title.toLowerCase().includes(searchLower) ||
               insight.description.toLowerCase().includes(searchLower)
             );
    }
    
    return true;
  });

  // Handle create new card
  const handleCreateCard = () => {
    setShowCreateModal(true);
  };

  // Handle create card submit
  const handleCreateCardSubmit = (cardData: any) => {
    const budgetAmount = cardData.budget || 100000;
    const newCard: SmartCard = {
      id: Date.now().toString(),
      title: cardData.title,
      type: cardData.type,
      status: 'active',
      aiScore: Math.floor(Math.random() * 30) + 70, // Random score between 70-100
      insights: [
        {
          id: '1',
          type: 'optimization',
          title: 'تحليل أولي',
          description: 'تم إنشاء البطاقة بنجاح وجاري تحليل البيانات',
          confidence: 85,
          impact: 'medium',
          category: 'عام',
          actionRequired: false,
          timeframe: '24 ساعة',
          priority: 2
        }
      ],
      metrics: {
        budget: budgetAmount,
        spent: 0,
        efficiency: 100,
        roi: 0,
        trend: 0
      },
      lastAnalyzed: new Date(),
      nextAnalysis: new Date(Date.now() + 24 * 60 * 60 * 1000),
      // Additional properties for preview display
      budget: `${budgetAmount.toLocaleString()} ر.س`,
      spent: '0 ر.س',
      remaining: `${budgetAmount.toLocaleString()} ر.س`,
      utilization: 0,
      trend: 'stable',
      trendValue: '0%',
      alerts: 0,
      recommendations: 1,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      performance: 'new',
      lastUpdate: 'الآن',
      budgetBreakdown: {
        allocated: budgetAmount,
        spent: 0,
        remaining: budgetAmount,
        percentage: 0
      },
      customRecommendations: [
        {
          type: 'optimization',
          title: 'بدء التحليل',
          description: 'ابدأ بإضافة البيانات والمعاملات لتحصل على توصيات ذكية مخصصة',
          impact: 'medium',
          savings: '0 ر.س'
        }
      ]
    };
    
    setCards(prev => [...prev, newCard]);
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <DashboardLayout currentPage="smart-cards">
        <div className="flex items-center justify-center min-h-screen">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"
          />
          <span className="mr-4 text-lg font-medium text-gray-700 dark:text-gray-300">
            جاري تحليل البيانات بالذكاء الاصطناعي...
          </span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="smart-cards">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl">
              <SparklesIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                البطاقات الذكية
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                تحليل ذكي مدعوم بالذكاء الاصطناعي لتحسين الأداء المالي
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <button 
              onClick={handleCreateCard}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2" />
              إنشاء بطاقة جديدة
            </button>
            
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' 
                    ? 'bg-white dark:bg-gray-700 shadow-sm' 
                    : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Squares2X2Icon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white dark:bg-gray-700 shadow-sm' 
                    : 'hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <ListBulletIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-4 rtl:space-x-reverse">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث في البطاقات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rtl:pl-4 rtl:pr-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">جميع البطاقات</option>
              <option value="department">الأقسام</option>
              <option value="project">المشاريع</option>
              <option value="campaign">الحملات</option>
              <option value="branch">الفروع</option>
            </select>
          </div>
          
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {filteredCards.length} من {cards.length} بطاقة ذكية
          </div>
        </div>

        {/* Smart Cards Grid */}
        <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
          {filteredCards.map((card) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              {/* Card Header */}
              <div className={`bg-gradient-to-r ${getStatusColor(card.status)} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 rtl:space-x-reverse">
                    <CpuChipIcon className="w-6 h-6" />
                    <div>
                      <h3 className="font-bold text-lg">{card.title}</h3>
                      <p className="text-sm opacity-90">
                        نقاط الذكاء الاصطناعي: {card.aiScore}/100
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{card.aiScore}</div>
                    <div className="text-xs opacity-75">AI Score</div>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6 space-y-4">
                {/* Metrics */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <BanknotesIcon className="w-5 h-5 text-green-600" />
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400">الميزانية</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {formatCurrency(card.metrics.budget)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <ChartBarIcon className="w-5 h-5 text-blue-600" />
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400">الكفاءة</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">
                          {card.metrics.efficiency}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Insights */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900 dark:text-white flex items-center">
                      <LightBulbIcon className="w-5 h-5 ml-2 rtl:ml-0 rtl:mr-2 text-yellow-500" />
                      رؤى الذكاء الاصطناعي
                    </h4>
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full">
                      {card.insights.length} رؤية
                    </span>
                  </div>
                  
                  {card.insights.slice(0, 2).map((insight: any) => (
                    <motion.div
                      key={insight.id}
                      className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${getImpactColor(insight.impact)}`}>
                              {insight.impact === 'high' ? 'تأثير عالي' : 
                               insight.impact === 'medium' ? 'تأثير متوسط' : 'تأثير منخفض'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ثقة {insight.confidence}%
                            </span>
                          </div>
                          <h5 className="font-medium text-gray-900 dark:text-white mb-1">
                            {insight.title}
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                            {insight.description}
                          </p>
                          {(insight.estimatedSavings || insight.estimatedRevenue) && (
                            <div className="mt-2 text-sm font-semibold">
                              {insight.estimatedSavings && (
                                <span className="text-green-600 dark:text-green-400">
                                  توفير متوقع: {formatCurrency(insight.estimatedSavings)}
                                </span>
                              )}
                              {insight.estimatedRevenue && (
                                <span className="text-blue-600 dark:text-blue-400">
                                  إيراد متوقع: {formatCurrency(insight.estimatedRevenue)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {insight.actionRequired && (
                          <ExclamationTriangleIcon className="w-5 h-5 text-orange-500 flex-shrink-0" />
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center space-x-2 rtl:space-x-reverse text-xs text-gray-500 dark:text-gray-400">
                    <ClockIcon className="w-4 h-4" />
                    <span>آخر تحليل: منذ {Math.floor((Date.now() - card.lastAnalyzed.getTime()) / 60000)} دقيقة</span>
                  </div>
                  
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <button className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      <ArrowPathIcon className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      <CogIcon className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => setSelectedCard(card)}
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors"
                    >
                      عرض التفاصيل
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* AI Analysis Engine */}
        <div className="space-y-6">
          <AIAnalysisEngine 
            entityId="department-1" 
            entityType="department"
          />
          
          {/* Smart Metrics */}
          <SmartMetrics 
            entityId="department-1" 
            entityType="department"
          />
          
          {/* Smart Recommendations */}
          <SmartRecommendations 
            entityId="department-1" 
            entityType="department"
            maxRecommendations={5}
          />
        </div>

        {/* Card Details Modal */}
        <AnimatePresence>
          {selectedCard && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedCard(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedCard.title}
                    </h2>
                    <button
                      onClick={() => setSelectedCard(null)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      ✕
                    </button>
                  </div>
                  
                  {/* Detailed insights would go here */}
                  <div className="space-y-6">
                    {selectedCard.insights.map((insight: any) => (
                      <div key={insight.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                          {insight.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-3">
                          {insight.description}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 rtl:space-x-reverse">
                            <span className={`text-xs px-2 py-1 rounded-full ${getImpactColor(insight.impact)}`}>
                              {insight.impact === 'high' ? 'تأثير عالي' : 
                               insight.impact === 'medium' ? 'تأثير متوسط' : 'تأثير منخفض'}
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              ثقة {insight.confidence}%
                            </span>
                          </div>
                          {insight.actionRequired && (
                            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                              اتخاذ إجراء
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Create Card Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowCreateModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    إنشاء بطاقة ذكية جديدة
                  </h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target as HTMLFormElement);
                    const cardData = {
                      title: formData.get('title') as string,
                      type: formData.get('type') as string,
                      budget: parseInt(formData.get('budget') as string) || 100000
                    };
                    handleCreateCardSubmit(cardData);
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      اسم البطاقة
                    </label>
                    <input
                      type="text"
                      name="title"
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="مثال: قسم المبيعات"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      نوع البطاقة
                    </label>
                    <select
                      name="type"
                      required
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="department">قسم</option>
                      <option value="project">مشروع</option>
                      <option value="campaign">حملة تسويقية</option>
                      <option value="branch">فرع</option>
                      <option value="supplier">مورد</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الميزانية المخصصة (ريال سعودي)
                    </label>
                    <input
                      type="number"
                      name="budget"
                      min="1000"
                      step="1000"
                      defaultValue="100000"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="100000"
                    />
                  </div>

                  <div className="flex items-center justify-end space-x-3 rtl:space-x-reverse pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                    >
                      إلغاء
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      إنشاء البطاقة
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default SmartCardsPage;
